import React from "react";
import { Box, Grid, Typography, Chip } from "@mui/material";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";

interface StatusCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  variant: "error" | "warning" | "success";
  isSelected: boolean;
  onClick: () => void;
}

function StatusCard({ title, value, icon, variant, isSelected, onClick }: StatusCardProps) {
  const getVariantColors = () => {
    switch (variant) {
      case "error":
        return {
          border: "#EF4444",
          bg: isSelected ? "#FEF2F2" : "#FFFFFF",
          borderWidth: isSelected ? "2px" : "1px",
          iconColor: "#EF4444",
        };
      case "warning":
        return {
          border: "#F59E0B",
          bg: isSelected ? "#FFFBEB" : "#FFFFFF",
          borderWidth: isSelected ? "2px" : "1px",
          iconColor: "#F59E0B",
        };
      case "success":
        return {
          border: "#10B981",
          bg: isSelected ? "#F0FDF4" : "#FFFFFF",
          borderWidth: isSelected ? "2px" : "1px",
          iconColor: "#10B981",
        };
    }
  };

  const colors = getVariantColors();

  return (
    <Box
      onClick={onClick}
      sx={{
        height: "100%",
        minHeight: 80,
        p: 2,
        backgroundColor: colors.bg,
        borderRadius: 2,
        border: `${colors.borderWidth} solid ${colors.border}`,
        boxShadow: isSelected 
          ? "0 4px 12px rgba(0,0,0,0.12)" 
          : "0 1px 3px rgba(0,0,0,0.06)",
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500, fontSize: "0.813rem", mb: 0.5 }}
          >
            {title}
          </Typography>
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontSize: "1.75rem",
              fontWeight: 700,
              color: colors.iconColor,
              lineHeight: 1,
            }}
          >
            {value}
          </Typography>
        </Box>
        <Box sx={{ color: colors.iconColor, display: "flex", opacity: 0.8 }}>
          {icon}
        </Box>
      </Box>
    </Box>
  );
}

interface StatusCardsProps {
  progressData: {
    lateDeadline: number;
    todayDeadline: number;
    onSchedule: number;
  };
  selectedProgress: { value: number } | undefined;
  onSelectProgress: (value: number, name: string) => void;
}

export default function StatusCards({
  progressData,
  selectedProgress,
  onSelectProgress,
}: StatusCardsProps) {
  const handleFilterClick = (buttonSelectedProgress: number) => {
    let name;

    switch (buttonSelectedProgress) {
      case 1:
        name = "em atraso";
        break;
      case 2:
        name = "até hoje";
        break;
      case 3:
        name = "no prazo";
        break;
      case 0:
        name = "sem prazo Maximo";
        break;
      default:
        name = "";
        break;
    }

    onSelectProgress(buttonSelectedProgress, name);
  };

  const isSelected = (value: number) => selectedProgress?.value === value;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <StatusCard
          title="Em atraso"
          value={progressData.lateDeadline}
          icon={<AssignmentLateOutlinedIcon fontSize="large" />}
          variant="error"
          isSelected={isSelected(1)}
          onClick={() => handleFilterClick(1)}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <StatusCard
          title="Hoje"
          value={progressData.todayDeadline}
          icon={<AssignmentOutlinedIcon fontSize="large" />}
          variant="warning"
          isSelected={isSelected(2)}
          onClick={() => handleFilterClick(2)}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <StatusCard
          title="No prazo"
          value={progressData.onSchedule}
          icon={<AssignmentTurnedInOutlinedIcon fontSize="large" />}
          variant="success"
          isSelected={isSelected(3)}
          onClick={() => handleFilterClick(3)}
        />
      </Grid>
    </Grid>
  );
}
